'use client';

import { Checkbox } from '@/components/ui/checkbox';
import { Field, FieldDescription, FieldLabel } from '@/components/ui/field';
import { Controller, type UseFormReturn } from 'react-hook-form';

import type { OfferFormValues } from '../components/offer-form-schema';

/** Minimal package shape the picker needs. */
export interface OfferPickerPackage {
  id: string;
  name: string;
  isActive: boolean;
  sellOnline: boolean;
}

/**
 * The packages a promotion discounts. Its regular price is the package price,
 * and online booking shows the promoted price on packages sold online.
 */
export function OfferPackagesPicker({
  form,
  packages,
}: {
  form: UseFormReturn<OfferFormValues>;
  packages: OfferPickerPackage[];
}) {
  // Tolerate a not-yet-array list (a stub or a stale cache shape).
  const live = Array.isArray(packages)
    ? packages.filter((pkg) => pkg.isActive)
    : [];
  if (live.length === 0) return null;
  return (
    <Controller
      control={form.control}
      name="packageIds"
      render={({ field }) => {
        const selected = field.value ?? [];
        const toggle = (id: string, checked: boolean) =>
          field.onChange(
            checked
              ? [...selected.filter((value) => value !== id), id]
              : selected.filter((value) => value !== id)
          );
        return (
          <Field>
            <FieldLabel>Packages</FieldLabel>
            <FieldDescription>
              The saving is taken off the package price. Packages sold online
              show it on your booking page.
            </FieldDescription>
            <div className="flex flex-col rounded-lg border">
              {live.map((pkg) => (
                // biome-ignore lint/a11y/noLabelWithoutControl: label wraps a Radix Checkbox (renders a button, not a native input), so implicit association isn't statically detected
                <label
                  key={pkg.id}
                  className="flex cursor-pointer items-center gap-3 border-b px-3 py-3 last:border-b-0 hover:bg-muted/40"
                >
                  <Checkbox
                    aria-label={pkg.name}
                    checked={selected.includes(pkg.id)}
                    onCheckedChange={(checked) =>
                      toggle(pkg.id, checked === true)
                    }
                  />
                  <span className="flex flex-1 flex-col">
                    <span className="font-medium">{pkg.name}</span>
                    {!pkg.sellOnline && (
                      <span className="text-xs text-muted-foreground">
                        Not sold online
                      </span>
                    )}
                  </span>
                </label>
              ))}
            </div>
          </Field>
        );
      }}
    />
  );
}
